import { useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { DEFAULT_UNIT_SYSTEM, isUnitSystem, type UnitSystem } from '@jupi/shared'

// Whether weight & balance figures are shown metric (the default) or in the
// flight manual's imperial units, for comparing against the handbook. One
// per-viewer preference, remembered in this browser, shared by every page
// that shows them.

const KEY = 'jupi.unitSystem'
const listeners = new Set<() => void>()

function read(): UnitSystem {
  try {
    const stored = localStorage.getItem(KEY)
    return isUnitSystem(stored) ? stored : DEFAULT_UNIT_SYSTEM
  } catch {
    return DEFAULT_UNIT_SYSTEM
  }
}

let current: UnitSystem = read()

export function setUnitSystem(next: UnitSystem) {
  current = next
  try {
    localStorage.setItem(KEY, next)
  } catch {
    // Private browsing: it lasts until the page is reloaded.
  }
  for (const listener of listeners) listener()
}

export function useUnitSystem(): UnitSystem {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => current,
    () => DEFAULT_UNIT_SYSTEM,
  )
}

export function UnitSystemToggle({ size = 'small', compact = false }: { size?: 'small' | 'medium'; compact?: boolean }) {
  const { t } = useTranslation()
  const system = useUnitSystem()
  return (
    <ToggleButtonGroup
      exclusive
      size={size}
      value={system}
      aria-label={t('units.label')}
      onChange={(_, value: UnitSystem | null) => value && setUnitSystem(value)}
    >
      <ToggleButton value="metric" sx={{ px: 2 }}>
        {compact ? 'kg' : t('units.metric')}
      </ToggleButton>
      <ToggleButton value="imperial" sx={{ px: 2 }}>
        {compact ? 'lbs' : t('units.imperial')}
      </ToggleButton>
    </ToggleButtonGroup>
  )
}
